"use strict";
/**
 * Pill Selector
 *
 * A single-choice row of tap targets, used where a native `<select>` used to
 * be (events-page data source, PNL venue). One tap switches instead of
 * open-then-pick-then-close — the operator is on a touchscreen.
 *
 * The pills are declared in `public/index.html`; this only wires them:
 *
 *   <div id="data-source" class="pill-selector" role="radiogroup" ...>
 *     <button type="button" class="pill" role="radio"
 *             data-value="kalshi" aria-checked="true">Kalshi</button>
 *     ...
 *   </div>
 *
 * `aria-checked` IS the selection — the stylesheet hooks the same attribute, so
 * there is no parallel class to drift out of sync with the accessible state.
 *
 * A pill marked `hidden` is withheld: it can't be tapped or arrowed onto, but
 * its value still works. That is how a venue is taken off the menu without
 * deleting the code behind it (the legacy Polymarket one).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.initPillSelector = initPillSelector;
/** Arrow-key step for a keyboard user; 0 for any other key. */
function arrowStep(key) {
    if (key === "ArrowRight" || key === "ArrowDown")
        return 1;
    if (key === "ArrowLeft" || key === "ArrowUp")
        return -1;
    return 0;
}
/**
 * Wire the pill group at `root`. Returns null when the group is absent or
 * empty, so callers can null-guard it exactly like a missing element.
 */
function initPillSelector(root) {
    if (!root)
        return null;
    const pills = Array.from(root.querySelectorAll("[data-value]"));
    if (pills.length === 0)
        return null;
    const handlers = [];
    /**
     * The pills a user can actually reach.
     *
     * Two independent things can withhold a pill and BOTH must be honoured:
     * the `hidden` attribute (the markup's own switch, used for legacy
     * Polymarket) and `.permission-gated-hidden` (applied by gates.ts for a
     * venue the tier may not see). Checking only `hidden` left a gated pill
     * invisible but still arrow-reachable — and arrowing onto it fires
     * `onChange`, switching the operator to a venue they cannot see.
     */
    const offered = () => pills.filter((pill) => !pill.hidden &&
        !pill.classList.contains("permission-gated-hidden"));
    // Trust the markup's own checked pill; fall back to the first one on offer
    // so the group is never rendered with nothing selected.
    let selected = (pills.find((pill) => pill.getAttribute("aria-checked") === "true") ??
        offered()[0] ??
        pills[0]).dataset.value ?? "";
    const paint = () => {
        for (const pill of pills) {
            const isSelected = pill.dataset.value === selected;
            pill.setAttribute("aria-checked", String(isSelected));
            // Roving tabindex: the group is one tab stop, arrows move within it.
            pill.tabIndex = isSelected ? 0 : -1;
        }
    };
    const select = (value, notify) => {
        if (value === selected)
            return;
        const pill = pills.find((candidate) => candidate.dataset.value === value);
        if (!pill)
            return;
        // Selecting a withheld pill re-offers it: the operator is on that venue
        // (stored state can still name it), so it has to show — and give them a
        // way back off it — rather than leave the group looking unselected.
        pill.hidden = false;
        selected = value;
        paint();
        if (notify)
            for (const handler of handlers)
                handler(value);
    };
    root.addEventListener("click", (event) => {
        const pill = event.target.closest("[data-value]");
        // `closest` can walk past the group; only pills of THIS group count.
        if (pill && pills.includes(pill))
            select(pill.dataset.value ?? "", true);
    });
    root.addEventListener("keydown", (event) => {
        const step = arrowStep(event.key);
        if (step === 0)
            return;
        event.preventDefault();
        const reachable = offered();
        if (reachable.length === 0)
            return;
        const index = reachable.findIndex((pill) => pill.dataset.value === selected);
        const next = reachable[(index + step + reachable.length) % reachable.length];
        select(next.dataset.value ?? "", true);
        next.focus();
    });
    paint();
    return {
        get value() {
            return selected;
        },
        setValue: (value) => select(value, false),
        onChange: (handler) => {
            handlers.push(handler);
        },
    };
}
